import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/config/supabase';
import { queryClient } from '@/config/queryClient';
import type { OrgMember, Organisation, PlatformAdmin } from '@/types';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface AuthState {
  member: OrgMember | null;
  organisation: Organisation | null;
  // Set only for a cross-organisation Super Admin/Co-Admin account — never
  // alongside `member` (platform admins have no org_members row at all).
  // LoginPage/RootRedirect send these accounts to /platform instead of
  // /dashboard.
  platformAdmin: PlatformAdmin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;
  // True when the organisation's security settings require MFA for this
  // member and the current session has not yet satisfied it (FR-005).
  // LoginPage redirects to /mfa-verify while this is true rather than
  // treating the member as fully signed in. Never set for platform admins —
  // org-level MFA settings don't apply to a cross-org account.
  mfaRequired: boolean;

  login: (email: string, password: string) => Promise<void>;
  completeMfaChallenge: () => Promise<void>;
  logout: () => Promise<void>;
  setMember: (member: OrgMember | null, organisation: Organisation | null) => void;
  clearError: () => void;
  resetPassword: (email: string) => Promise<void>;
}

// Maps a normalized error code to user-friendly copy. `code` (not the
// message text) is the source of truth for UI branching.
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials: 'Invalid email or password. Please try again.',
  invalid_grant: 'Invalid email or password. Please try again.',
  user_not_found: 'No account found with this email address.',
  no_active_membership: 'Your account is not an active member of any organisation.',
  over_request_rate_limit: 'Too many attempts. Please wait a moment and try again.',
  over_email_send_rate_limit: 'Too many emails sent. Please wait before requesting another.',
  network_error: 'Network error. Please check your connection.',
  server_error: 'Server error. Please try again later.',
};

function resolveAuthError(error: unknown): { code: string; message: string } {
  const err = error as { message?: string; code?: string } | undefined;
  const rawMessage = (err?.message || '').toLowerCase();

  if (rawMessage.includes('invalid login credentials')) {
    return { code: 'invalid_credentials', message: AUTH_ERROR_MESSAGES.invalid_credentials! };
  }

  const code = err?.code || err?.message || 'unknown_error';
  const message = AUTH_ERROR_MESSAGES[code] || err?.message || 'An unexpected error occurred. Please try again.';
  return { code, message };
}

// Resolves the caller's active org_members row (+ organisation) after
// Supabase Auth succeeds. Kept inline here (not a separate service module)
// matching the B2C reference app's authStore pattern, so login/the auth
// listener/OAuth callbacks can't drift apart on how a member is resolved.
async function fetchActiveMembership(
  authUser: SupabaseUser
): Promise<{ member: OrgMember | null; organisation: Organisation | null }> {
  const { data: member } = await supabase
    .from('org_members')
    .select('*')
    .eq('user_id', authUser.id)
    .eq('status', 'active')
    .maybeSingle();

  if (!member) {
    return { member: null, organisation: null };
  }

  const { data: organisation } = await supabase
    .from('organisations')
    .select('*')
    .eq('id', member.organisation_id)
    .maybeSingle();

  return { member: member as OrgMember, organisation: (organisation ?? null) as Organisation | null };
}

// Checked BEFORE fetchActiveMembership in login()/the auth listener — a
// platform admin has no org_members row, so org-scoped resolution would
// otherwise send them straight to "no_active_membership".
async function fetchPlatformAdmin(authUser: SupabaseUser): Promise<PlatformAdmin | null> {
  const { data } = await supabase.from('platform_admins').select('*').eq('user_id', authUser.id).eq('is_active', true).maybeSingle();
  return (data ?? null) as PlatformAdmin | null;
}

/** Races a promise against a timeout so a hung network call (e.g. during a
 * Supabase outage) can never leave the caller stuck forever. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('Request timed out')), ms)),
  ]);
}

async function resolveMfaRequired(member: OrgMember, organisation: Organisation | null): Promise<boolean> {
  const requiresMfa =
    !!organisation?.settings?.require_mfa_all ||
    (!!organisation?.settings?.require_mfa_admin && (member.role === 'owner' || member.role === 'admin'));

  if (!requiresMfa) return false;

  // If the org requires MFA, the session must actually have satisfied a
  // second factor (aal2), not just have one enrolled.
  const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  return data?.currentLevel !== 'aal2';
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      member: null,
      organisation: null,
      platformAdmin: null,
      isAuthenticated: false,
      // Not persisted (see partialize below) -- always starts true on a
      // fresh page load, even though `isAuthenticated`/`member` above are
      // restored instantly from localStorage. This keeps ProtectedRoute
      // showing a loading state until initAuthListener() below has actually
      // confirmed the real Supabase session is still valid, instead of
      // rendering protected pages (and firing their data queries) against a
      // persisted "authenticated" flag that may already be stale.
      isLoading: true,
      error: null,
      errorCode: null,
      mfaRequired: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null, errorCode: null });

        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email: email.trim().toLowerCase(),
            password,
          });

          if (error) throw error;
          if (!data.user) throw new Error('Login failed. Please try again.');

          const platformAdmin = await fetchPlatformAdmin(data.user);
          if (platformAdmin) {
            set({ platformAdmin, member: null, organisation: null, isAuthenticated: true, mfaRequired: false, isLoading: false });
            return;
          }

          const { member, organisation } = await fetchActiveMembership(data.user);

          if (!member) {
            await supabase.auth.signOut();
            throw { code: 'no_active_membership' };
          }

          const mfaRequired = await resolveMfaRequired(member, organisation);

          set({
            member,
            organisation,
            platformAdmin: null,
            isAuthenticated: !mfaRequired,
            mfaRequired,
            isLoading: false,
          });
        } catch (error: unknown) {
          console.error('Login error:', error);
          const resolved = resolveAuthError(error);
          set({ error: resolved.message, errorCode: resolved.code, isLoading: false });
        }
      },

      // Called by MfaVerifyPage after the Supabase Auth MFA challenge/verify
      // call succeeds — flips the member from "credentials verified" to
      // "fully authenticated" (FR-005).
      completeMfaChallenge: async () => {
        set({ isAuthenticated: true, mfaRequired: false });
      },

      resetPassword: async (email: string) => {
        set({ isLoading: true, error: null, errorCode: null });

        try {
          const { error } = await supabase.auth.resetPasswordForEmail(
            email.trim().toLowerCase(),
            { redirectTo: `${window.location.origin}/reset-password` }
          );

          if (error) throw error;
          set({ isLoading: false });
        } catch (error: unknown) {
          console.error('Reset password error:', error);
          const resolved = resolveAuthError(error);
          set({ error: resolved.message, errorCode: resolved.code, isLoading: false });
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          // Bounded: if Supabase's own servers are degraded/unreachable
          // (e.g. a platform-side outage), signOut() can hang indefinitely
          // with no error and no success -- which would leave the Sign Out
          // button stuck forever with no way to recover except manually
          // clearing browser storage. 5s is generous for a normal network
          // round trip but short enough that a hang still resolves quickly.
          await withTimeout(supabase.auth.signOut(), 5000);
        } catch (error) {
          // Even if the server call fails or times out, drop local state so
          // the UI never stays "logged in" with a dead session.
          console.error('Logout error:', error);
        } finally {
          set({
            member: null,
            organisation: null,
            platformAdmin: null,
            isAuthenticated: false,
            mfaRequired: false,
            error: null,
            errorCode: null,
            isLoading: false,
          });
          // Drop cached org-scoped data so the next account signing in on
          // this browser never sees this member's cached queries.
          queryClient.clear();
        }
      },

      setMember: (member, organisation) => {
        set({ member, organisation, isAuthenticated: !!member });
      },

      clearError: () => {
        set({ error: null, errorCode: null });
      },
    }),
    {
      name: 'decixa-b2b-auth',
      partialize: (state) => ({
        member: state.member,
        organisation: state.organisation,
        platformAdmin: state.platformAdmin,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// ============================================================================
// AUTH STATE LISTENER
// ============================================================================
// Must be called once at app startup. Keeps the Zustand store in sync with
// the Supabase session lifecycle: expiry, sign-out (including other tabs),
// and token refresh.
let authListenerStarted = false;

export const initAuthListener = () => {
  if (authListenerStarted) return;
  authListenerStarted = true;

  // IMPORTANT: this callback must stay synchronous. supabase-js holds an
  // internal auth lock while dispatching these events; any Supabase call in
  // here (a query, getSession, ...) would wait on that same lock and
  // deadlock every future getSession() call in the app. Async work is
  // deferred with setTimeout so it runs after the lock is released — a
  // lesson carried over from the B2C reference app.
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      const wasAuthenticated = useAuthStore.getState().isAuthenticated;
      useAuthStore.setState({ member: null, organisation: null, platformAdmin: null, isAuthenticated: false, mfaRequired: false, isLoading: false });
      if (wasAuthenticated) queryClient.clear();
      return;
    }

    if (!session) {
      // Always resolve isLoading here, not just when isAuthenticated was
      // already true -- isLoading now starts true on every fresh page load
      // (see the store's initial state above), including for a visitor with
      // no persisted session at all. Without this unconditional reset, a
      // brand-new visitor would be stuck on the loading screen forever,
      // since nothing else in this event would ever flip it to false.
      useAuthStore.setState({ member: null, organisation: null, platformAdmin: null, isAuthenticated: false, isLoading: false });
      return;
    }

    // On INITIAL_SESSION, Zustand persist already holds the member/platform
    // admin — skip the DB re-fetch, but only when it's genuinely the SAME
    // user. Without the user_id check, a still-logged-in User A who clicks
    // User B's invite/magic link (establishing a brand new session whose
    // session.user is really User B) would keep this shortcut firing and
    // never re-fetch — leaving the UI stuck showing User A's stale
    // member/org data under User B's actual auth session.
    if (event === 'INITIAL_SESSION') {
      const persistedMember = useAuthStore.getState().member;
      const persistedPlatformAdmin = useAuthStore.getState().platformAdmin;
      const sameUser = (persistedMember?.user_id ?? persistedPlatformAdmin?.user_id) === session.user?.id;
      if ((persistedMember || persistedPlatformAdmin) && sameUser) {
        useAuthStore.setState({ isLoading: false });
        return;
      }
    }
    if (!session.user) {
      useAuthStore.setState({ isLoading: false });
      return;
    }

    const sessionUser = session.user;
    setTimeout(async () => {
      // Each DB call is individually bounded so a degraded/unreachable
      // Supabase backend (a hung request neither resolves nor rejects on
      // its own) can't leave isLoading stuck true forever — the try/finally
      // below only helps once a promise actually settles.
      try {
        const platformAdmin = await withTimeout(fetchPlatformAdmin(sessionUser), 8000);
        if (event !== 'INITIAL_SESSION' && !useAuthStore.getState().isAuthenticated) return;

        if (platformAdmin) {
          useAuthStore.setState({ platformAdmin, member: null, organisation: null, isAuthenticated: true });
          return;
        }

        const { member, organisation } = await withTimeout(fetchActiveMembership(sessionUser), 8000);

        // Guard against a race: if the member signed out while this fetch
        // was in-flight, don't re-authenticate them.
        if (event !== 'INITIAL_SESSION' && !useAuthStore.getState().isAuthenticated) return;

        if (member) {
          useAuthStore.getState().setMember(member, organisation);
        } else if (event === 'INITIAL_SESSION') {
          // A real session exists but resolves to no platform admin and no
          // active org membership -- nothing to authenticate into.
          useAuthStore.setState({ isAuthenticated: false });
        }
      } catch (error) {
        console.error('Auth listener membership fetch failed:', error);
        // A timeout/failure here on the very first load must not leave the
        // app stuck showing a stale "authenticated" shell against a session
        // we were never able to actually confirm.
        if (event === 'INITIAL_SESSION') {
          useAuthStore.setState({ member: null, organisation: null, platformAdmin: null, isAuthenticated: false });
        }
      } finally {
        useAuthStore.setState({ isLoading: false });
      }
    }, 0);
  });

  // Final safety net: if nothing above ever resolves isLoading (e.g.
  // onAuthStateChange itself never fires an INITIAL_SESSION event during a
  // platform-side outage), don't leave the app on an infinite loading
  // screen -- fall back to a clean, logged-out state after a generous
  // window. This is exactly the state a manual "clear site data" currently
  // has to be used to reach by hand.
  setTimeout(() => {
    if (useAuthStore.getState().isLoading) {
      useAuthStore.setState({ member: null, organisation: null, platformAdmin: null, isAuthenticated: false, isLoading: false });
    }
  }, 12000);
};
